/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { AuthService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';
import { SWITCH } from 'app/config/switch';

import { type DataConfig, DataTypeE, GlobalSearchStrService } from '../../global-search-str.service';

@Injectable({ providedIn: 'root' })
export class ConfigInsightStrService {
    insightDataMap: { [key: string]: DataConfig } = {
        /**
         * 告警
         */
        [DataTypeE.alarm]: {
            type: DataTypeE.alarm,
            url: '/monitor/alarms-and-events',
            tab: this.str.i18n.alarmTab,
            page: {
                searchKey: 'monitor.alarm_term_realTime_label',
                permission: ['alarm_R'],
                defaultAdvancedSearch: true,
                advancedType: this.str.i18n.monitorTitle,
            },
        },
        /**
         * 事件
         */
        [DataTypeE.event]: {
            type: DataTypeE.event,
            url: '/monitor/alarms-and-events',
            tab: this.str.i18n.eventTab,
            page: {
                searchKey: 'monitor.alarm_term_type_event_label',
                permission: ['alarm_R'],
            },
        },
        /**
         * 诊断任务
         */
        [DataTypeE.DIAGNOSIS_TASK]: {
            type: DataTypeE.DIAGNOSIS_TASK,
            url: '/monitor/fault-tree-diagnosis',
            hide: !SWITCH.SUPPORT_FAULT_TREE_DIAGNOSIS,
            page: {
                searchKey: 'monitor.diagnosis_task_diagnosisTask_tab',
                permission: ['fault_diagnose_R'],
            },
            operation: [
                {
                    searchKey: 'monitor.diagnosis_task_diagnosisTask_create_title',
                    action: 'create',
                    permission: ['fault_diagnose_W'],
                },
            ],
        },
        /**
         * 监控面板
         */
        [DataTypeE.dashboard]: {
            type: DataTypeE.dashboard,
            url: '/monitor/performance/dashboard',
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            page: {
                searchKey: 'common.dashboard_title',
                permission: ['performance_R'],
                defaultAdvancedSearch: true,
                advancedType: this.str.i18n.monitorTitle,
            },
        },
        /**
         * 分析
         */
        [DataTypeE.analysis]: {
            type: DataTypeE.analysis,
            url: '/monitor/performance/analysis',
            page: {
                searchKey: 'monitor.charts_term_chart_label',
                permission: ['performance_R'],
            },
        },
        /**
         * 异常检测
         */
        [DataTypeE.anomaly]: {
            type: DataTypeE.anomaly,
            url: '/monitor/performance/performance-anomaly',
            isSmartGUIFeature: true,
            hide: !(SWITCH.SUPPORT_ABNORMAL_DETECTION && SWITCH.PRODUCT_SUPPORT_AI),
            page: {
                searchKey: 'monitor.anomalyDetect_task_title',
                permission: ['performance_R'],
            },
        },
        /**
         * 功耗查看
         */
        [DataTypeE.powerConsumption]: {
            type: DataTypeE.powerConsumption,
            url: '/monitor/performance/power-consumption',
            hide: !FeatureConfig.IsSupportPowerView,
            page: {
                searchKey: 'common.powerConsumption_title',
                permission: ['power_supply_R'],
            },
        },
        /**
         * 预测
         */
        [DataTypeE.forecasting]: {
            type: DataTypeE.forecasting,
            url: '/monitor/forecasting',
            hide: !SWITCH.SUPPORT_CAPACITY_PREDICT,
            isNotSupportFeatureInOceanMicro: !SWITCH.SUPPORT_CAPACITY_PREDICT,
            page: {
                searchKey: 'common.forecasting_newtitle',
                permission: ['performance_R'],
            },
        },
        /**
         * 预测-容量
         */
        [DataTypeE.capacityForecast]: {
            type: DataTypeE.capacityForecast,
            url: '/monitor/forecasting',
            hide: !SWITCH.SUPPORT_CAPACITY_PREDICT,
            tab: this.str.i18n.capacityForecastTab,
            isNotSupportFeatureInOceanMicro: !SWITCH.SUPPORT_CAPACITY_PREDICT,
            page: {
                searchKey: 'monitor.forecasting_task_capForecast_tab',
                permission: ['performance_R'],
                defaultAdvancedSearch: true,
                advancedType: this.str.i18n.monitorTitle,
            },
        },
        /**
         * 预测-性能
         */
        [DataTypeE.performanceForecast]: {
            type: DataTypeE.performanceForecast,
            url: '/monitor/forecasting',
            hide: !SWITCH.SUPPORT_PERFORMANCE_PREDICT,
            tab: this.str.i18n.performanceForecastTab,
            isSmartGUIFeature: true,
            isNotSupportFeatureInOceanMicro: !SWITCH.SUPPORT_CAPACITY_PREDICT,
            page: {
                searchKey: 'common.forecasting_title',
                permission: ['performance_R'],
            },
        },
        /**
         * 报表任务
         */
        [DataTypeE.dataExport]: {
            type: DataTypeE.dataExport,
            url: '/monitor/schedule-report',
            tab: this.str.i18n.dataExportTaskTab,
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            hide: this.authService.isRemoteAssistanceUser(),
            operation: [
                {
                    searchKey: 'home.search_term_createReport_lab',
                    action: 'create',
                    permission: ['performance_R', 'performance_W'],
                },
            ],
            page: {
                searchKey: this.str.i18n.dataExportTaskTab,
                permission: ['performance_R'],
            },
        },
        /**
         * 报表模版
         */
        [DataTypeE.reportTemplate]: {
            type: DataTypeE.reportTemplate,
            url: '/monitor/schedule-report',
            tab: this.str.i18n.dataExportTemplateTab,
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            hide: this.authService.isRemoteAssistanceUser(),
            page: {
                searchKey: this.str.i18n.dataExportTemplateTab,
                permission: ['performance_R'],
            },
        },
        /**
         * 任务中心
         */
        [DataTypeE.taskCenter]: {
            type: DataTypeE.taskCenter,
            url: '/monitor/task',
            page: {
                searchKey: 'common.taskCenter_title',
                permission: ['task_R'],
            },
        },
    };

    constructor(
        private str: GlobalSearchStrService,
        private authService: AuthService,
    ) { }

    /**
     * 返回dataMap
     */
    getDataMap(): { [key: string]: DataConfig } {
        return this.insightDataMap;
    }
}
